import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { getIdToken } from '../lib/auth';

/**
 * ChatFAB — floating chatbot launcher.
 *
 * Mounted once in AppShell. Hidden on /admin/* and /register; the parent
 * already gates rendering on auth + paywall, so we only need to gate on
 * route here.
 *
 * Streaming: uses fetch + ReadableStream (NOT EventSource) so we can
 * authenticate via Authorization header. The server emits SSE-formatted
 * `data:` lines which we parse line-by-line.
 *
 * Continue where left off: on first open, fetches /api/chat/active and
 * hydrates the message list with the last conversation if it's < 24h.
 */

type ServerMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
};

type LocalMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
  // True while the assistant is still streaming. Used to render a
  // typing-indicator-style cursor and disable the send button.
  streaming?: boolean;
};

const HIDDEN_PATHS = ['/admin', '/register', '/auth/'];

export function ChatFAB() {
  const { t, i18n } = useTranslation(['chat', 'common']);
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<LocalMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // End-of-conversation rating UI. Shown over the body when user clicks
  // the trash icon on a conversation with ≥3 turns. Skippable.
  const [ratingOpen, setRatingOpen] = useState(false);
  const [ratingValue, setRatingValue] = useState<number>(0);
  const [ratingComment, setRatingComment] = useState('');
  const [ratingBusy, setRatingBusy] = useState(false);
  const [endNotice, setEndNotice] = useState<string | null>(null);
  const [quotaWarn, setQuotaWarn] = useState(false);
  const [quotaBlocked, setQuotaBlocked] = useState(false);
  // Report-an-issue modal. Open via flag icon in head; submits the
  // current transcript + expected-behaviour note to the maintainer.
  const [reportOpen, setReportOpen] = useState(false);
  const [reportText, setReportText] = useState('');
  const [reportBusy, setReportBusy] = useState(false);

  const turnCount = useMemo(
    () => messages.filter((m) => m.role === 'user' && !m.id.startsWith('local-asst-')).length,
    [messages],
  );

  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Draggable FAB position. Stored as {right, bottom} in CSS pixels —
  // not {x, y} — so RTL/LTR don't flip the saved value. null until the
  // user has dragged at least once; in that state the FAB sits at the
  // CSS default. Drag distance < DRAG_THRESHOLD_PX is treated as a
  // click (open/close) instead of a position change.
  const FAB_POS_KEY = 'lexcrm.chat.fabPos';
  const DRAG_THRESHOLD_PX = 6;
  const [fabPos, setFabPos] = useState<{ right: number; bottom: number } | null>(() => {
    try {
      const raw = localStorage.getItem(FAB_POS_KEY);
      if (!raw) return null;
      const v = JSON.parse(raw);
      if (typeof v?.right === 'number' && typeof v?.bottom === 'number') {
        return { right: v.right, bottom: v.bottom };
      }
    } catch {
      /* ignore */
    }
    return null;
  });
  const dragStateRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    startRight: number;
    startBottom: number;
    moved: boolean;
  } | null>(null);

  function clampPos(right: number, bottom: number): { right: number; bottom: number } {
    const fabSize = 56;
    const maxRight = Math.max(0, window.innerWidth - fabSize);
    const maxBottom = Math.max(0, window.innerHeight - fabSize);
    return {
      right: Math.min(maxRight, Math.max(0, right)),
      bottom: Math.min(maxBottom, Math.max(0, bottom)),
    };
  }

  function handleFabPointerDown(e: React.PointerEvent<HTMLButtonElement>) {
    // Establish a drag baseline. We don't preventDefault here so a quick
    // click still fires onClick. Movement beyond threshold flips into
    // drag mode and the click handler suppresses itself.
    const cur = fabPos ?? readFabRect(e.currentTarget);
    dragStateRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      startRight: cur.right,
      startBottom: cur.bottom,
      moved: false,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  }

  function handleFabPointerMove(e: React.PointerEvent<HTMLButtonElement>) {
    const s = dragStateRef.current;
    if (!s || s.pointerId !== e.pointerId) return;
    const dx = e.clientX - s.startX;
    const dy = e.clientY - s.startY;
    if (!s.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
    s.moved = true;
    // Inverted axes: dragging RIGHT (positive dx) decreases the
    // distance from the right edge. Same for bottom: dragging DOWN
    // decreases distance from the bottom edge.
    setFabPos(clampPos(s.startRight - dx, s.startBottom - dy));
  }

  function handleFabPointerUp(e: React.PointerEvent<HTMLButtonElement>) {
    const s = dragStateRef.current;
    if (!s || s.pointerId !== e.pointerId) return;
    e.currentTarget.releasePointerCapture(e.pointerId);
    if (s.moved && fabPos) {
      try { localStorage.setItem(FAB_POS_KEY, JSON.stringify(fabPos)); } catch { /* ignore */ }
    }
    dragStateRef.current = null;
  }

  function handleFabClick(e: React.MouseEvent<HTMLButtonElement>) {
    // If we just finished a drag, swallow the click — the user moved
    // the FAB, they didn't mean to open it.
    if (dragStateRef.current?.moved) {
      e.preventDefault();
      e.stopPropagation();
      dragStateRef.current = null;
      return;
    }
    toggle();
  }

  function readFabRect(el: HTMLElement): { right: number; bottom: number } {
    const r = el.getBoundingClientRect();
    return {
      right: window.innerWidth - r.right,
      bottom: window.innerHeight - r.bottom,
    };
  }

  const hidden = useMemo(() => HIDDEN_PATHS.some((p) => location.pathname.startsWith(p)), [
    location.pathname,
  ]);

  // First open: fetch /chat/active to populate any in-flight conversation.
  useEffect(() => {
    if (!open || hydrated) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch<{
          configured: boolean;
          isImpersonating: boolean;
          quota: { used: number; softLimit: number; hardLimit: number; blocked: boolean; warn: boolean };
          conversation: {
            id: string;
            startedAt: string;
            lastMessageAt: string;
            messages: ServerMessage[];
          } | null;
        }>('/api/chat/active');
        if (cancelled) return;
        setConfigured(res.configured);
        setIsImpersonating(res.isImpersonating);
        setQuotaWarn(res.quota.warn);
        setQuotaBlocked(res.quota.blocked);
        if (res.conversation) {
          setConversationId(res.conversation.id);
          setMessages(res.conversation.messages);
        }
      } catch {
        if (!cancelled) setConfigured(false);
      } finally {
        if (!cancelled) setHydrated(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, hydrated]);

  // Auto-focus + scroll to bottom whenever the panel opens or messages change.
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [open]);
  useEffect(() => {
    if (!bodyRef.current) return;
    bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, open]);

  function toggle() {
    setOpen((p) => !p);
  }

  async function clearConversation() {
    if (!conversationId) {
      setMessages([]);
      return;
    }
    // Conversations with ≥3 user turns route through the rating modal —
    // they're substantive enough that a transcript+summary email is
    // useful. Shorter sessions just clear without ceremony.
    if (turnCount >= 3) {
      setRatingValue(0);
      setRatingComment('');
      setRatingOpen(true);
      return;
    }
    try {
      await apiFetch(`/api/chat/conversations/${conversationId}/clear`, { method: 'POST' });
    } catch {
      // Best-effort. Server-side stays 'active' until next /send rolls it.
    }
    setConversationId(null);
    setMessages([]);
    setError(null);
  }

  async function endWithRating(rating: number | null) {
    if (!conversationId || ratingBusy) return;
    setRatingBusy(true);
    try {
      const res = await apiFetch<{ emailed: boolean; summary: string | null }>(
        `/api/chat/conversations/${conversationId}/end`,
        {
          method: 'POST',
          body: JSON.stringify({
            ...(rating !== null ? { rating } : {}),
            ...(ratingComment.trim() ? { ratingComment: ratingComment.trim() } : {}),
          }),
        },
      );
      setEndNotice(res.emailed ? t('chat:end_emailed') : t('chat:end_no_email'));
    } catch (err) {
      setEndNotice((err as Error).message ?? t('chat:end_failed'));
    } finally {
      setRatingBusy(false);
      setRatingOpen(false);
      setConversationId(null);
      setMessages([]);
      setError(null);
      setTimeout(() => setEndNotice(null), 4000);
    }
  }

  async function submitReport() {
    if (!conversationId || reportBusy) return;
    const expected = reportText.trim();
    if (!expected) return;
    setReportBusy(true);
    try {
      await apiFetch(`/api/chat/conversations/${conversationId}/report`, {
        method: 'POST',
        body: JSON.stringify({ expected, path: location.pathname }),
      });
      setEndNotice(t('chat:report_sent'));
      setReportOpen(false);
      setReportText('');
    } catch (err) {
      setEndNotice((err as Error).message ?? t('chat:report_failed'));
    } finally {
      setReportBusy(false);
      setTimeout(() => setEndNotice(null), 4000);
    }
  }

  async function sendMessage(text: string) {
    if (!text.trim() || sending) return;
    setError(null);
    const optimisticUser: LocalMessage = {
      id: `local-${Date.now()}`,
      role: 'user',
      content: text,
      createdAt: new Date().toISOString(),
    };
    const placeholder: LocalMessage = {
      id: `local-asst-${Date.now()}`,
      role: 'assistant',
      content: '',
      createdAt: new Date().toISOString(),
      streaming: true,
    };
    setMessages((prev) => [...prev, optimisticUser, placeholder]);
    setSending(true);
    setInput('');

    const ctrl = new AbortController();
    abortRef.current = ctrl;
    // When on a case-detail page (/cases/:id), pass the caseId so the
    // server can attach a per-case data block to the system prompt.
    // Match the route the SPA uses; we accept any URL with /cases/<uuid-ish>.
    const caseMatch = /^\/cases\/([a-zA-Z0-9-]+)/.exec(location.pathname);
    const caseId = caseMatch?.[1];
    try {
      const idToken = (await getIdToken()) ?? '';
      const res = await fetch('/api/chat/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          conversationId,
          text,
          ...(caseId ? { caseId } : {}),
        }),
        signal: ctrl.signal,
      });
      if (!res.ok || !res.body) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error ?? `http_${res.status}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split('\n');
        buf = lines.pop() ?? '';
        for (const line of lines) {
          const m = /^data: (.+)$/.exec(line);
          if (!m) continue;
          let evt: { type: string; [k: string]: unknown };
          try {
            evt = JSON.parse(m[1]!);
          } catch {
            continue;
          }
          if (evt.type === 'open') {
            const cid = evt['conversationId'] as string;
            setConversationId(cid);
          } else if (evt.type === 'chunk') {
            const chunk = (evt['text'] as string) ?? '';
            setMessages((prev) =>
              prev.map((m) =>
                m.id === placeholder.id ? { ...m, content: m.content + chunk } : m,
              ),
            );
          } else if (evt.type === 'done') {
            setMessages((prev) =>
              prev.map((m) => (m.id === placeholder.id ? { ...m, streaming: false } : m)),
            );
          } else if (evt.type === 'error') {
            throw new Error((evt['message'] as string) ?? 'stream_error');
          }
        }
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
      setError((err as Error).message ?? 'send_failed');
      // Drop the placeholder so the user can retry.
      setMessages((prev) => prev.filter((m) => m.id !== placeholder.id));
    } finally {
      setSending(false);
      abortRef.current = null;
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void sendMessage(input);
    }
  }

  if (hidden) return null;

  const showWelcome = open && hydrated && messages.length === 0;
  const quickChips: { label: string; text: string }[] = [
    { label: t('chat:quick.urgent_cases.label'), text: t('chat:quick.urgent_cases.text') },
    { label: t('chat:quick.open_invoices.label'), text: t('chat:quick.open_invoices.text') },
    { label: t('chat:quick.tasks_today.label'), text: t('chat:quick.tasks_today.text') },
    { label: t('chat:quick.new_clients.label'), text: t('chat:quick.new_clients.text') },
  ];

  const dir = i18n.language === 'he' ? 'rtl' : 'ltr';

  return (
    <>
      <button
        className={`chat-fab ${open ? 'open' : ''}`}
        onClick={handleFabClick}
        onPointerDown={handleFabPointerDown}
        onPointerMove={handleFabPointerMove}
        onPointerUp={handleFabPointerUp}
        onPointerCancel={handleFabPointerUp}
        style={
          fabPos
            ? {
                // Property order matters here. `right` and
                // `inset-inline-end` map to the same effective slot in
                // LTR — the LATER declaration wins. We clear all
                // logical / legacy slots first, THEN set the physical
                // values, so our pixel position can't be nuked by an
                // 'auto' that follows it. Same for top/left/bottom on
                // the y-axis. Touch-action keeps mobile drags from
                // triggering scroll.
                insetInlineStart: 'auto',
                insetInlineEnd: 'auto',
                left: 'auto',
                top: 'auto',
                right: `${fabPos.right}px`,
                bottom: `${fabPos.bottom}px`,
                touchAction: 'none',
              }
            : { touchAction: 'none' }
        }
        aria-label={t(open ? 'chat:close' : 'chat:open')}
        title={t(open ? 'chat:close' : 'chat:open')}
      >
        <svg className="icon-chat" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
        </svg>
        <svg className="icon-close" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>

      <div className={`chat-window ${open ? 'open' : ''}`} dir={dir}>
        <div className="chat-head">
          <div className="bot-av">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <circle cx="12" cy="8" r="4" />
              <path d="M6 20v-2a6 6 0 0112 0v2" />
            </svg>
          </div>
          <div className="bot-info">
            <div className="bot-name">{t('chat:title')}</div>
            <div className="bot-status">
              <span className="status-dot" />
              {isImpersonating ? t('chat:impersonating') : t('chat:online')}
            </div>
          </div>
          <div className="head-actions">
            <button
              className="head-btn"
              onClick={() => {
                setReportText('');
                setReportOpen(true);
              }}
              disabled={!conversationId || messages.length === 0}
              title={t('chat:report')}
              aria-label={t('chat:report')}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 21V4h12l-2 4 2 4H4" />
                <line x1="4" y1="21" x2="4" y2="13" />
              </svg>
            </button>
            <button
              className="head-btn"
              onClick={() => void clearConversation()}
              title={t('chat:clear')}
              aria-label={t('chat:clear')}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6l-1 14H6L5 6" />
                <path d="M10 11v6M14 11v6" />
              </svg>
            </button>
            <button className="head-btn" onClick={toggle} title={t('chat:close')} aria-label={t('chat:close')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        {showWelcome && configured !== false && (
          <div className="quick-wrap">
            {quickChips.map((c) => (
              <button
                key={c.label}
                className="qchip"
                onClick={() => void sendMessage(c.text)}
                disabled={sending}
              >
                {c.label}
              </button>
            ))}
          </div>
        )}

        <div className="chat-body" ref={bodyRef}>
          {showWelcome && (
            <div className="msg bot">
              <div className="bubble">{t('chat:welcome')}</div>
            </div>
          )}
          {messages.map((m) => (
            <div key={m.id} className={`msg ${m.role}`}>
              <div className="bubble">
                {m.content}
                {m.streaming && <span className="cursor" />}
              </div>
            </div>
          ))}
          {configured === false && (
            <div className="msg bot">
              <div className="bubble">{t('chat:not_configured')}</div>
            </div>
          )}
          {quotaBlocked && (
            <div className="msg bot">
              <div className="bubble" style={{ background: 'var(--red-bg)', color: 'var(--red)' }}>
                {t('chat:quota_blocked')}
              </div>
            </div>
          )}
          {quotaWarn && !quotaBlocked && (
            <div className="msg bot">
              <div className="bubble" style={{ background: 'var(--amber-bg)', color: 'var(--amber)' }}>
                {t('chat:quota_warn')}
              </div>
            </div>
          )}
          {endNotice && (
            <div className="msg bot">
              <div className="bubble" style={{ background: 'var(--green-bg)', color: 'var(--green)' }}>
                {endNotice}
              </div>
            </div>
          )}
          {error && (
            <div className="msg bot">
              <div className="bubble" style={{ color: 'var(--red)' }}>
                {t('chat:error_prefix')}: {error}
              </div>
            </div>
          )}
        </div>

        {reportOpen && (
          <div className="rating-overlay">
            <div className="rating-card">
              <div className="rating-title">{t('chat:report_modal.title')}</div>
              <textarea
                className="rating-input"
                value={reportText}
                onChange={(e) => setReportText(e.target.value)}
                placeholder={t('chat:report_modal.placeholder')}
                rows={4}
                maxLength={2000}
                autoFocus
              />
              <div className="rating-actions">
                <button
                  className="btn-cancel"
                  onClick={() => setReportOpen(false)}
                  disabled={reportBusy}
                >
                  {t('chat:report_modal.cancel')}
                </button>
                <button
                  className="btn-save"
                  onClick={() => void submitReport()}
                  disabled={reportBusy || !reportText.trim()}
                >
                  {reportBusy ? t('common:actions.loading_dots') : t('chat:report_modal.submit')}
                </button>
              </div>
            </div>
          </div>
        )}

        {ratingOpen && (
          <div className="rating-overlay">
            <div className="rating-card">
              <div className="rating-title">{t('chat:rating.title')}</div>
              <div className="rating-stars">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`rating-star ${ratingValue >= n ? 'filled' : ''}`}
                    onClick={() => setRatingValue(n)}
                    aria-label={`${n}`}
                  >
                    ★
                  </button>
                ))}
              </div>
              <textarea
                className="rating-input"
                value={ratingComment}
                onChange={(e) => setRatingComment(e.target.value)}
                placeholder={t('chat:rating.comment_placeholder')}
                rows={2}
                maxLength={500}
              />
              <div className="rating-actions">
                <button
                  className="btn-cancel"
                  onClick={() => void endWithRating(null)}
                  disabled={ratingBusy}
                >
                  {t('chat:rating.skip')}
                </button>
                <button
                  className="btn-save"
                  onClick={() => void endWithRating(ratingValue || null)}
                  disabled={ratingBusy}
                >
                  {ratingBusy ? t('common:actions.loading_dots') : t('chat:rating.submit')}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="chat-footer">
          <textarea
            ref={inputRef}
            className="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={t('chat:input_placeholder')}
            rows={1}
            disabled={sending || configured === false}
          />
          <button
            className="send-btn"
            onClick={() => void sendMessage(input)}
            disabled={!input.trim() || sending || configured === false}
            aria-label={t('chat:send')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </div>
        <div className="chat-powered">{t('chat:powered_by')}</div>
      </div>
    </>
  );
}
